import axios from 'axios';
import type {
  FleetDashboardSummary,
  FleetFilterOptions,
  FleetFilters,
  FleetScope,
  Vehicle,
  VehicleListResponse
} from '../types/Vehicle';
import { VehicleApiError } from './vehicleService';
import { sharedRequest } from '../utils/sharedRequest';

const BASE_URL = process.env.REACT_APP_API_URL || '/api';

function apiError(error: unknown): Error {
  const err = error as { response?: { status?: number; data?: { error?: string; message?: string; fields?: Record<string, string> } }; request?: unknown; message?: string };
  if (err?.response) {
    const data = err.response.data || {};
    return new VehicleApiError(data.error || data.message || 'Something went wrong. Try again.', err.response.status, data.fields || {});
  }
  if (err?.request) return new Error('No response from the server. Check your connection.');
  return new Error(err?.message || 'Something went wrong. Try again.');
}

/** Query string from a filter object, skipping empty values and keeping commas readable. */
export function fleetQuery(values: Record<string, string | number | undefined | null>): string {
  const params = new URLSearchParams();
  Object.entries(values).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') params.set(key, String(value));
  });
  return params.toString().replace(/%2C/gi, ',');
}

/**
 * Fleet list, Shop board and Dashboard calls (EQ-14, EQ-16): the filters take comma-separated
 * Service Area / Foreman Area ids and the Dashboard tile filters.
 */
const equipmentFleetService = {
  async getFleet(page: number, pageSize: number, filters: FleetFilters = {}): Promise<VehicleListResponse> {
    try {
      const query = fleetQuery({ page, pageSize, ...(filters as Record<string, string | undefined>) });
      // V3-036: one request per distinct list query, even when asked twice at once.
      return await sharedRequest(`vehicles?${query}`, async () => (await axios.get(`${BASE_URL}/vehicles?${query}`)).data);
    } catch (error) {
      throw apiError(error);
    }
  },

  async getFilterOptions(): Promise<FleetFilterOptions> {
    try {
      const data = await sharedRequest('vehicles/filter-options', async () => (await axios.get(`${BASE_URL}/vehicles/filter-options`)).data);
      return { ...data, foremanAreas: data.foremanAreas ?? [], myForemanAreaIds: data.myForemanAreaIds ?? [] };
    } catch (error) {
      throw apiError(error);
    }
  },

  async getShopBoard(scope: FleetScope = {}): Promise<Vehicle[]> {
    try {
      const query = fleetQuery({ serviceArea: scope.serviceArea, foremanArea: scope.foremanArea });
      return (await axios.get(`${BASE_URL}/vehicles/shop-board${query ? `?${query}` : ''}`)).data;
    } catch (error) {
      throw apiError(error);
    }
  },

  async getDashboardSummary(scope: FleetScope = {}): Promise<FleetDashboardSummary> {
    try {
      const query = fleetQuery({ serviceArea: scope.serviceArea, foremanArea: scope.foremanArea });
      return (await axios.get(`${BASE_URL}/vehicles/dashboard-summary${query ? `?${query}` : ''}`)).data;
    } catch (error) {
      throw apiError(error);
    }
  }
};

export default equipmentFleetService;
